<template>
  <app-filter-toolbar>
    <template v-if="$slots.create" #create><slot name="create" /></template>
    <template v-if="$slots.actions" #actions><slot name="actions" /></template>
    <template #primary>
      <app-input v-model="form.search" design="new" type="search" placeholder="Number, subject or requester" aria-label="Search" />
    </template>
    <app-select v-model="form.system" design="new" :options="systems" label="name" placeholder="System" aria-label="System" />
    <app-select v-model="form.direction" design="new" :options="directions" label="name" placeholder="Direction" aria-label="Direction" />
    <app-select v-model="form.module" design="new" :options="modules" label="name" placeholder="Module" aria-label="Module" />
    <app-select v-model="form.tags" design="new" :options="tags" multiple label="name" placeholder="Tags" aria-label="Tags" />
    <app-select v-model="form.labels" design="new" :options="labels" multiple label="name" placeholder="Responsible labels" aria-label="Responsible labels" />
    <app-select v-model="form.assignee" design="new" :options="assignees" multiple label="full_name" placeholder="Assignee" aria-label="Assignee" />
    <app-select v-model="form.priority" design="new" :options="priorityOptions" multiple label="label" placeholder="Priority" aria-label="Priority" />
    <template #selection>
      <app-filters v-model="form.importance" design="new" refined weight="medium" name="support-ticket-importance" aria-label="Important" :filters="importanceOptions" />
    </template>
    <template #secondary-selection>
      <div class="support-ticket-filters__toggles">
        <app-checkbox v-model="form.unread" design="new" label="Unread only" />
        <app-checkbox v-model="form.unassigned" design="new" label="Unassigned only" />
      </div>
    </template>
    <template v-if="hasFilters" #clear>
      <AppClearFiltersButton design="new" icon-only @clear="clear" />
    </template>
  </app-filter-toolbar>
</template>

<script>
import { getSupportTicketDictionary, getSupportTicketUsers } from '@/http'
import { normalizePage, PRIORITY_OPTIONS } from '../ticketState'

const emptyForm = () => ({
  search: '',
  assignee: [],
  system: null,
  direction: null,
  module: null,
  tags: [],
  labels: [],
  priority: [],
  importance: 'all',
  unread: false,
  unassigned: false
})

export default {
  name: 'SupportTicketFilters',
  data() {
    return {
      form: emptyForm(),
      systems: [],
      directions: [],
      modules: [],
      tags: [],
      labels: [],
      assignees: [],
      priorityOptions: PRIORITY_OPTIONS,
      importanceOptions: [
        { value: 'all', label: 'All' },
        { value: 'true', label: 'Important', tone: 'amber' },
        { value: 'false', label: 'Not important' }
      ],
      timer: null
    }
  },
  computed: {
    hasFilters() {
      const form = this.form
      return Boolean(form.search || form.system || form.direction || form.module || form.tags.length || form.labels.length || form.assignee.length || form.priority.length || form.importance !== 'all' || form.unread || form.unassigned)
    }
  },
  watch: {
    form: { deep: true, handler() { window.clearTimeout(this.timer); this.timer = window.setTimeout(this.emit, 250) } },
    'form.system'(value) {
      if (this.form.direction?.system !== value?.id) { this.form.direction = null; this.form.module = null }
      this.loadDirections()
    },
    'form.direction'(value) {
      if (this.form.module?.direction !== value?.id) this.form.module = null
      this.loadModules()
    }
  },
  created() {
    Promise.all([
      getSupportTicketDictionary('systems', { active: true }),
      getSupportTicketDictionary('tags', { active: true }),
      getSupportTicketDictionary('responsible_labels', { active: true }),
      getSupportTicketUsers({ kind: 'assignee', page_size: 100 })
    ]).then(([systems, tags, labels, users]) => {
      this.systems = normalizePage(systems.data)
      this.tags = normalizePage(tags.data)
      this.labels = normalizePage(labels.data)
      this.assignees = normalizePage(users.data)
    })
    this.form.unread = this.$route.query.unread === 'true'
    this.form.unassigned = this.$route.query.unassigned === 'true'
    this.form.priority = this.priorityOptions.filter(option => (this.$route.query.priority || '').split(',').includes(option.value))
    this.form.importance = ['true', 'false'].includes(this.$route.query.is_important) ? this.$route.query.is_important : 'all'
  },
  beforeDestroy() { window.clearTimeout(this.timer) },
  methods: {
    loadDirections() {
      if (!this.form.system) { this.directions = []; return }
      getSupportTicketDictionary('directions', { active: true, system: this.form.system.id })
        .then(response => { this.directions = normalizePage(response.data) })
    },
    loadModules() {
      if (!this.form.direction) { this.modules = []; return }
      getSupportTicketDictionary('modules', { active: true, direction: this.form.direction.id })
        .then(response => { this.modules = normalizePage(response.data) })
    },
    emit() {
      const values = this.form
      this.$emit('change', {
        search: values.search || undefined,
        assignee: values.assignee.map(item => item.id).join(',') || undefined,
        system: values.system?.id,
        direction: values.direction?.id,
        module: values.module?.id,
        tags: values.tags.map(item => item.id).join(',') || undefined,
        responsible_labels: values.labels.map(item => item.id).join(',') || undefined,
        priority: values.priority.map(item => item.value).join(',') || undefined,
        is_important: values.importance === 'all' ? undefined : values.importance,
        unread: values.unread || undefined,
        unassigned: values.unassigned || undefined
      })
    },
    clear() { this.form = emptyForm(); this.$emit('change', {}) }
  }
}
</script>

<style lang="scss" scoped>
.support-ticket-filters__toggles { display: flex; align-items: center; flex-wrap: wrap; gap: $ui-space-12; }
</style>
